import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuoteTemplate } from '../../../../hooks/useTemplateSettings';
import { useCompanyStore } from '../../../../stores/companyStore';
import { QuoteEditData } from '../../../../hooks/useQuoteEdit';
import { QuoteTemplateClassicEdit } from './QuoteTemplateClassicEdit';
import { QuoteTemplateModernEdit } from './QuoteTemplateModernEdit';
import { QuoteTemplateCorporateEdit } from './QuoteTemplateCorporateEdit';
import { QuoteTemplateElegantEdit } from './QuoteTemplateElegantEdit';
import { logger } from '../../../../utils/logger';

interface QuoteTemplateEditRouterProps {
  initialData: QuoteEditData;
}

/**
 * Router pour les templates éditables de devis
 * Sélectionne le bon template selon le nom du template en base de données
 *
 * Tous les templates sont maintenant disponibles en mode édition :
 * - Classique (FREE)
 * - Design Your Brand (STARTER)
 * - Agency (STARTER)
 * - Élégant (BUSINESS)
 */
export const QuoteTemplateEditRouter: React.FC<QuoteTemplateEditRouterProps> = ({
  initialData,
}) => {
  const { t } = useTranslation();
  const { template, loading, error } = useQuoteTemplate();
  const { company } = useCompanyStore();

  logger.log('🔍 QuoteTemplateEditRouter - company:', company);
  logger.log('🔍 QuoteTemplateEditRouter - initialData:', initialData);

  // Chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 p-8 hover:bg-white/35 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <p className="text-gray-700 font-light tracking-wide">{t('templates.common.loadingTemplate')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Erreur - utiliser template classique par défaut
  if (error) {
    logger.warn('Erreur chargement template:', error);
    return (
      <QuoteTemplateClassicEdit
        initialData={initialData}
        template={template || undefined}
        company={company}
        color={template?.primaryColor || '#0d9488'}
      />
    );
  }

  // Sélectionner le template selon le nom
  const templateName = template?.name || 'default';

  switch (templateName) {
    case 'default':
      return (
        <QuoteTemplateClassicEdit
          initialData={initialData}
          template={template || undefined}
          company={company}
          color={template?.primaryColor || '#0d9488'}
        />
      );

    case 'design_your_brand':
      return (
        <QuoteTemplateModernEdit
          initialData={initialData}
          template={template || undefined}
          company={company}
          color={template?.primaryColor || '#0d9488'}
        />
      );

    case 'agency':
      return (
        <QuoteTemplateCorporateEdit
          initialData={initialData}
          template={template || undefined}
          company={company}
          color={template?.primaryColor || '#000000'}
        />
      );

    case 'elegant':
      return (
        <QuoteTemplateElegantEdit
          initialData={initialData}
          template={template || undefined}
          company={company}
          color={template?.primaryColor || '#D4AF37'}
        />
      );

    default:
      // Fallback vers template classique pour tout template inconnu
      logger.warn(`Template inconnu: ${templateName}, utilisation du template classique`);
      return (
        <QuoteTemplateClassicEdit
          initialData={initialData}
          template={template || undefined}
          company={company}
          color={template?.primaryColor || '#0d9488'}
        />
      );
  }
};
